/**
 * Custom hook for managing group details state and operations
 */

import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { apiClient } from '@/config/apiConfig';
import type { Group } from '@/api/data-contracts';
import { useSnackbar } from '@/contexts/SnackbarContext';

interface GroupMember {
  user_id: string;
  role: string;
  joined_at: string;
  joined_by: string;
}

interface UseGroupDetailsReturn {
  activeTab: number;
  setActiveTab: (tab: number) => void;
  syncing: boolean;
  error: string | null;
  handleSyncMembers: (group: Group & { members?: GroupMember[] }, onUpdate: () => void) => Promise<void>;
}

export const useGroupDetails = (open: boolean): UseGroupDetailsReturn => {
  const { t } = useTranslation();
  const { showSuccess, showError } = useSnackbar();
  const [activeTab, setActiveTab] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setActiveTab(0);
      setError(null);
    }
  }, [open]);

  const handleSyncMembers = async (group: Group & { members?: GroupMember[] }, onUpdate: () => void): Promise<void> => {
    if (group.source === 'local') {
      return;
    }

    try {
      setSyncing(true);
      setError(null);

      // Use the bulk sync endpoint for the specific group
      const response = await apiClient.http.request({
        path: `/api/groups/${group.id}/sync`,
        method: 'POST',
        secure: true,
      });

      // Show success message
      showSuccess(t('groups.details.syncSuccess'));

      // Refresh the group data
      onUpdate();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('groups.details.syncFailed');
      setError(errorMessage);
      showError(errorMessage);
    } finally {
      setSyncing(false);
    }
  };

  return {
    activeTab,
    setActiveTab,
    syncing,
    error,
    handleSyncMembers,
  };
};